import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useDrafts } from "../../contexts/DraftsContext";
import {
  Receipt,
  Plus,
  X,
  Trash2,
  Loader2,
  Fuel,
  Zap,
  Flame,
  Droplet,
  Home as HomeIcon,
  Wrench,
  Phone,
  Wifi,
  HelpCircle,
  Wallet,
  TrendingDown,
  Info,
  Search,
  Calendar,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; }
};

const CATEGORIES = [
  { id: "محروقات", icon: Fuel, color: "#E85D45" },
  { id: "كهرباء", icon: Zap, color: "#F5A623" },
  { id: "غاز", icon: Flame, color: "#EF4444" },
  { id: "ماء", icon: Droplet, color: "#3B82F6" },
  { id: "إيجار", icon: HomeIcon, color: "#7C3AED" },
  { id: "صيانة", icon: Wrench, color: "#0EA5E9" },
  { id: "اتصالات", icon: Phone, color: "#22C55E" },
  { id: "إنترنت", icon: Wifi, color: "#06B6D4" },
  { id: "أخرى", icon: HelpCircle, color: "#5C6E64" },
];
const catInfo = (name) => CATEGORIES.find((c) => c.id === name) || CATEGORIES[CATEGORIES.length - 1];

// ──────────── Add Expense Modal ────────────
const ExpenseModal = ({ onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("محروقات");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { requestClose } = useDrafts();

  const hasContent = !!(amount.trim() || note.trim());
  const guardedClose = () => requestClose(hasContent, "سند صرف جديد", onClose);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/expenses`, { category, amount: amt, note: note.trim() });
      toast.success("تم تسجيل سند الصرف");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="expense-modal" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#E85D45] to-[#F97316] text-white p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5" /> سند صرف جديد
          </h3>
          <button onClick={guardedClose} data-testid="expense-modal-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">نوع المصروف *</label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    data-testid={`expense-cat-${c.id}`}
                    className={`rounded-xl p-2 border-2 flex flex-col items-center gap-1 transition-colors ${
                      active ? "border-[#E85D45] bg-[#FEF2F2]" : "border-[#E4EBE6] hover:border-[#3B82F6]"
                    }`}
                  >
                    <Icon className="w-5 h-5" style={{ color: c.color }} />
                    <span className="text-[11px] font-bold">{c.id}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">المبلغ (د.ع) *</label>
            <input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="expense-amount" dir="ltr"
              className="w-full px-3 py-3 rounded-xl border-2 border-[#3B82F6] text-lg font-bold focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">ملاحظة (وصف المصروف)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="expense-note" rows={2}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]"
              placeholder={`مثلاً: بنزين سيارة السائق علي ١٠٠ لتر`} />
          </div>
          <p className="text-[11px] text-[#5C6E64] bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl p-2 flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#E85D45]" />
            هذا المصروف ينقص مباشرة من ربح اليوم ومن رأس المال.
          </p>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">
            إلغاء
          </button>
          <button onClick={submit} disabled={saving} data-testid="expense-submit"
            className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2 px-5 rounded-full flex items-center gap-1 disabled:bg-[#BAE6FD]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            تسجيل السند
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────── Main Tab ────────────
const ExpensesTab = ({ onChanged }) => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  // Feb 2026: search + date-range filters for the history log
  const [search, setSearch] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/expenses`),
        axios.get(`${API}/admin/expenses/summary`),
      ]);
      setExpenses(r1.data || []);
      setSummary(r2.data);
    } catch (e) {
      toast.error("فشل تحميل المصاريف");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refreshAll = () => { load(); onChanged && onChanged(); };

  // Client-side filter: search matches category/note; date-range compares
  // ISO created_at prefix (YYYY-MM-DD). Empty filters = pass-through.
  const filteredExpenses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter((v) => {
      if (q) {
        const hay = `${v.category || ""} ${v.note || ""} ${v.amount || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateFrom) {
        const d = (v.created_at || "").slice(0, 10);
        if (d < dateFrom) return false;
      }
      if (dateTo) {
        const d = (v.created_at || "").slice(0, 10);
        if (d > dateTo) return false;
      }
      return true;
    });
  }, [expenses, search, dateFrom, dateTo]);
  const filteredTotal = useMemo(
    () => filteredExpenses.reduce((s, v) => s + Number(v.amount || 0), 0),
    [filteredExpenses]
  );

  const removeExpense = async (id) => {
    if (!(await confirmDialog("حذف هذا السند؟ سيتم تحديث الأرباح ورأس المال."))) return;
    try {
      await axios.delete(`${API}/admin/expenses/${id}`);
      toast.success("تم الحذف");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    }
  };

  return (
    <div className="space-y-3" data-testid="expenses-tab" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#E85D45] to-[#F97316] text-white rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <TrendingDown className="w-5 h-5" />
          <h3 className="font-bold text-lg">المصاريف الكلية</h3>
        </div>
        <p className="text-xs opacity-90 mb-3">
          سندات الصرف ورواتب الموظفين — تنقص مباشرة من ربح اليوم ومن رأس المال.
        </p>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-[10px] opacity-90">اليوم</p>
            <p className="font-black text-lg mt-1" dir="ltr">{fmt(summary?.today.all)}</p>
          </div>
          <div className="bg-white/15 rounded-xl p-2.5 text-center">
            <p className="text-[10px] opacity-90">هذا الشهر</p>
            <p className="font-black text-lg mt-1" dir="ltr">{fmt(summary?.month.all)}</p>
          </div>
          <div className="bg-white/25 rounded-xl p-2.5 text-center">
            <p className="text-[10px] opacity-90">الإجمالي</p>
            <p className="font-black text-lg mt-1" dir="ltr">{fmt(summary?.total.all)}</p>
          </div>
        </div>
      </div>

      {/* Action button */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setAdding(true)} data-testid="open-expense-add"
          className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1">
          <Plus className="w-4 h-4" /> سند صرف جديد
        </button>
      </div>

      {/* Split breakdown */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Card label="سندات الصرف (محروقات/كهرباء/إلخ)" today={summary.today.manual} total={summary.total.manual} color="#E85D45" icon={Fuel} />
          <Card label="رواتب الموظفين المدفوعة" today={summary.today.salary} total={summary.total.salary} color="#3B82F6" icon={Wallet} />
        </div>
      )}

      {/* Per-category breakdown — big beautiful tiles with amount badges */}
      {summary && (
        <div className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6]">
          <h4 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-1.5">
            <TrendingDown className="w-4 h-4 text-[#E85D45]" /> المصاريف حسب النوع
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => {
              const found = (summary.categories || []).find((c) => c.category === cat.id);
              const amount = found?.amount || 0;
              const count = found?.count || 0;
              const Icon = cat.icon;
              const hasSpend = amount > 0;
              return (
                <div key={cat.id}
                  data-testid={`expense-cat-${cat.id}`}
                  className="relative rounded-2xl p-3 flex flex-col items-center text-center shadow-md hover:shadow-lg transition-all border-2 overflow-hidden"
                  style={{
                    background: hasSpend ? `linear-gradient(135deg, ${cat.color}18, ${cat.color}08)` : "#f8fafc",
                    borderColor: hasSpend ? `${cat.color}55` : "#e2e8f0",
                  }}>
                  {/* Decorative circle behind the icon */}
                  <div className="absolute -top-2 -left-2 w-14 h-14 rounded-full opacity-20"
                    style={{ background: hasSpend ? cat.color : "#94a3b8" }} />
                  <div className="relative z-10 flex flex-col items-center w-full">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-md mb-1.5"
                      style={{ background: `linear-gradient(135deg, ${cat.color}, ${cat.color}dd)` }}>
                      <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-black text-slate-800">{cat.id}</p>
                    <p className="font-black text-lg mt-0.5" dir="ltr"
                      style={{ color: hasSpend ? cat.color : "#94a3b8" }}>
                      {fmt(amount)}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {hasSpend ? `${count} سند` : "لا يوجد"}
                    </p>
                  </div>
                  {/* Badge on top-right showing count */}
                  {hasSpend && (
                    <div className="absolute top-1.5 right-1.5 min-w-[22px] h-[22px] rounded-full flex items-center justify-center px-1.5 shadow-md border-2 border-white text-[10px] font-black text-white"
                      style={{ background: cat.color }}>
                      {count}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6]">
        <h4 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-1">
          <Receipt className="w-4 h-4" /> سجل سندات الصرف ({filteredExpenses.length}
          {filteredExpenses.length !== expenses.length && <span className="text-slate-400"> من {expenses.length}</span>})
        </h4>

        {/* Filters: search + date-range */}
        <div className="space-y-2 mb-3">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="expense-search"
              placeholder="🔍 بحث بالنوع (محروقات، كهرباء...) أو الملاحظة أو المبلغ"
              className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-[#F5A623]/50 focus:border-[#F5A623] bg-white text-sm font-bold focus:outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="expense-search-clear"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
              >
                <X className="w-3 h-3 text-slate-500" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[140px] flex items-center gap-1">
              <label className="text-[10px] font-bold text-slate-600 shrink-0">من</label>
              <div className="relative flex-1">
                <Calendar className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#3B82F6] pointer-events-none" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="expense-date-from"
                  max={today}
                  className="w-full pr-7 pl-2 py-2 rounded-xl border-2 border-blue-100 focus:border-blue-400 text-xs focus:outline-none bg-white"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[140px] flex items-center gap-1">
              <label className="text-[10px] font-bold text-slate-600 shrink-0">إلى</label>
              <div className="relative flex-1">
                <Calendar className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#3B82F6] pointer-events-none" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="expense-date-to"
                  max={today}
                  className="w-full pr-7 pl-2 py-2 rounded-xl border-2 border-blue-100 focus:border-blue-400 text-xs focus:outline-none bg-white"
                  dir="ltr"
                />
              </div>
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                data-testid="expense-date-clear"
                className="text-[10px] font-bold px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center gap-1"
              >
                <X className="w-3 h-3" /> مسح
              </button>
            )}
          </div>
          {/* Filter summary banner */}
          {(search || dateFrom || dateTo) && (
            <div className="rounded-xl px-3 py-2 flex items-center justify-between text-xs bg-gradient-to-l from-blue-50 to-orange-50 border-2 border-blue-100">
              <span className="text-slate-700 font-bold">
                📊 نتائج الفلترة: <b className="text-blue-700">{filteredExpenses.length}</b> سند
              </span>
              <span className="font-black text-[#E85D45]" dir="ltr">
                −{fmt(filteredTotal)} د.ع
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-7 h-7 animate-spin inline text-[#E85D45]" /></div>
        ) : filteredExpenses.length === 0 ? (
          <p className="text-center text-[#5C6E64] text-sm py-6">
            {expenses.length === 0
              ? "لا توجد سندات صرف بعد. ابدأ بإضافة سند جديد."
              : "لا توجد نتائج مطابقة للفلترة الحالية."}
          </p>
        ) : (
          <div className="space-y-2">
            {filteredExpenses.map((v) => {
              const info = catInfo(v.category);
              const Icon = info.icon;
              // Colored row per category — matches the color of its icon.
              return (
                <div key={v.id} data-testid={`expense-row-${v.id}`}
                  className="rounded-xl p-3 flex items-center justify-between gap-2 border-2 shadow-sm hover:shadow-md transition-all relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${info.color}12, ${info.color}04)`,
                    borderColor: `${info.color}50`,
                  }}>
                  {/* Vertical color stripe on the right edge */}
                  <div className="absolute top-0 right-0 bottom-0 w-1.5" style={{ background: info.color }} />
                  <div className="flex items-center gap-2 flex-1 min-w-0 pr-1">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-md shrink-0"
                      style={{ background: `linear-gradient(135deg, ${info.color}, ${info.color}dd)` }}>
                      <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm" style={{ color: info.color }}>{v.category}</p>
                      <p className="text-[11px] text-slate-600 truncate">
                        {fmtDate(v.created_at)}
                        {v.note && <> · {v.note}</>}
                      </p>
                    </div>
                  </div>
                  <p className="font-black text-lg shrink-0" dir="ltr" style={{ color: info.color }}>−{fmt(v.amount)}</p>
                  <button onClick={() => removeExpense(v.id)} data-testid={`del-expense-${v.id}`}
                    className="text-red-500 hover:bg-white rounded-lg p-1.5 shrink-0 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {adding && <ExpenseModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refreshAll(); }} />}
    </div>
  );
};

const Card = ({ label, today, total, color, icon: Icon }) => (
  <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color }} />
      <p className="text-xs font-bold text-[#1E3A5F] truncate">{label}</p>
    </div>
    <div className="flex justify-between text-xs">
      <div>
        <p className="text-[10px] text-[#5C6E64]">اليوم</p>
        <p className="font-black text-base" style={{ color }} dir="ltr">{fmt(today)}</p>
      </div>
      <div className="text-left">
        <p className="text-[10px] text-[#5C6E64]">الإجمالي</p>
        <p className="font-black text-base" style={{ color }} dir="ltr">{fmt(total)}</p>
      </div>
    </div>
  </div>
);

export default ExpensesTab;
